import React from "react";

import { Slide, useMediaQuery, useTheme } from "@mui/material";
import { observer } from "mobx-react-lite";
import { makeStyles } from "tss-react/mui";

import FoodDatabase from "@/components/FoodDatabase";
import {
  AllergiesKind,
  FoodAddPortionDetailsFragment,
  FoodItemFragment,
} from "@/gql/graphql";

import { useRecipe } from "../store/RecipeStore";

// Reuses the meal-plan food search flyout (FoodDatabase) for adding recipe
// ingredients. Mirrors SearchFoodPanel's presentation: a plain Slide-in panel
// (NOT a Modal/Drawer) so the AddFoodPortionDialog that opens on top of it
// keeps its own focus trap without fighting a parent modal. Slides in from the
// right on desktop and fills the screen on mobile. Stays open after adding so
// multiple ingredients can be added in a row.
const useStyles = makeStyles()((theme) => ({
  desktopPanel: {
    position: "fixed",
    top: 0,
    right: 0,
    bottom: 0,
    width: 460,
    maxWidth: "100vw",
    zIndex: theme.zIndex.drawer,
    backgroundColor: theme.palette.common.white,
    boxShadow: theme.shadows[8],
    overflowY: "auto",
  },
  mobileOverlay: {
    position: "fixed",
    inset: 0,
    zIndex: theme.zIndex.drawer,
    backgroundColor: theme.palette.common.white,
    overflowY: "auto",
    WebkitOverflowScrolling: "touch",
  },
}));

const IngredientSearchPanel: React.FC = observer(() => {
  const { classes } = useStyles();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const recipe = useRecipe();

  const urlParams = new URLSearchParams(window.location.search);
  const queryParams = urlParams.get("allergies") ?? "";
  const clientAllergies = queryParams.length
    ? (queryParams.split(",") as AllergiesKind[])
    : [];

  const handleAddFood = (
    food: FoodItemFragment,
    foodPortion: FoodAddPortionDetailsFragment,
    amount: number
  ) => {
    recipe.addIngredientFromFood(food, foodPortion, amount);
  };

  const panel = (
    <FoodDatabase
      onClose={recipe.closeIngredientSearch}
      onAddFood={handleAddFood}
      clientAllergies={clientAllergies}
    />
  );

  if (isMobile) {
    return (
      <Slide
        in={recipe.isIngredientSearchOpen}
        direction="up"
        mountOnEnter
        unmountOnExit
      >
        <div className={classes.mobileOverlay}>{panel}</div>
      </Slide>
    );
  }

  return (
    <Slide
      in={recipe.isIngredientSearchOpen}
      direction="left"
      mountOnEnter
      unmountOnExit
    >
      <div className={classes.desktopPanel}>{panel}</div>
    </Slide>
  );
});

export default IngredientSearchPanel;
